import { Link } from "react-router-dom";
import { ArrowRight, Hand, Heart, Users, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import { PageHero } from "@/components/PageHero";
import { CTABand } from "@/components/CTABand";

const PATHS = [
  {
    icon: Heart,
    title: "I want to access support privately",
    desc: "No service referral or NDIS plan required.",
    cta: "Private Supports",
    to: "/private-supports",
    testid: "pathway-private",
    dark: true,
  },
  {
    icon: Hand,
    title: "I have an active NDIS plan",
    desc: "Request supports connected to your NDIS goals and available funding.",
    cta: "NDIS Supports",
    to: "/ndis-supports",
    testid: "pathway-ndis",
  },
  {
    icon: Users,
    title: "A service is supporting or funding the referral",
    desc: "For referrals and funding through DCP, education, community organisations or other services.",
    cta: "Service & Program Supports",
    to: "/referrals",
    testid: "pathway-service",
  },
  {
    icon: MessageCircle,
    title: "I'm not sure",
    desc: "That's okay. You don't need to understand the system before talking to us.",
    cta: "Yarn With Our Team",
    to: "/contact",
    testid: "pathway-yarn",
  },
];

const RequestSupport = () => (
  <div data-testid="request-support-page">
    <PageHero
      kicker="Request Support"
      title={"What brings you\nto Ngawilyi?"}
      lede="However you arrive, you'll be met the same way — with a yarn, not a form-shaped judgement. Choose the path that sounds like you."
    />
    <section className="px-6 pt-2 pb-2 bg-cream">
      <div className="max-w-6xl mx-auto">
        <p className="rounded-3xl bg-creamdark border border-ochre/30 px-7 py-5 text-sm sm:text-base text-earth leading-relaxed">
          <strong className="text-charcoal">You don't need to join a structured program to access individual support.</strong>{" "}
          However you arrive, initial yarns identify what matters to you — your goals, strengths, existing supports
          and the kind of engagement you're looking for.
        </p>
      </div>
    </section>
    <section className="px-6 pb-24 sm:pb-32 bg-cream">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
        {PATHS.map((p, i) => (
          <motion.div
            key={p.to}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              to={p.to}
              data-testid={p.testid}
              className={`group block rounded-[40px] p-10 h-full transition-transform duration-500 hover:-translate-y-2 ${
                p.dark ? "bg-terracotta text-cream" : "bg-creamdark border border-ochre/25 text-charcoal"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className={`rounded-2xl p-4 ${p.dark ? "bg-cream/15" : "bg-terracotta/15"}`}>
                  <p.icon className={`h-7 w-7 ${p.dark ? "text-cream" : "text-terracotta"}`} strokeWidth={1.5} />
                </div>
                <span className="font-display italic text-lg opacity-40">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h2 className="mt-8 font-display font-light text-3xl leading-snug">{p.title}</h2>
              <p className={`mt-3 leading-relaxed ${p.dark ? "text-cream/80" : "text-earth/85"}`}>{p.desc}</p>
              <span className={`mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest ${p.dark ? "text-cream" : "text-terracotta"}`}>
                {p.cta} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
    <CTABand
      title="Healing through story. Strength through connection."
      text="Narrative Therapy | Mentoring | Movement | Culture | Connection — woven together around what matters to you."
      primary={{ to: "/private-supports", label: "Private Supports" }}
      secondary={{ to: "/ndis-supports", label: "NDIS Supports" }}
    />
  </div>
);

export default RequestSupport;
